import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { DateRangePicker, Icons, PositionModal } from '@hmsexp/component-library';
import { get } from 'lodash';
import MultiSelect from './MultiSelect';
import TopicFilter from './TopicFilters';
import CancelButton from './CancelButton';
import TextInputWithAdornment from './InputTextWithAdornment';

const DynamicFilters = ({ filtersConfig, selectedFilters, onFiltersChange }) => {
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [customValues, setCustomValues] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  const [calendarParentPosition, setCalendarParentPosition] = useState(null);
  const customRef = React.createRef();
  const handleCustomOpen = () => {
    setCustomOpen(true);
    customRef.current.measure((fx, fy, width, height, px, py) => {
      setCalendarParentPosition({ x: px, y: py + 3, w: width, h: height });
    });
  };
  const handleSelect = (key, selected) => {
    onFiltersChange({
      ...selectedFilters,
      [key]: selected
    });
  };

  const handleClear = key => {
    const updated = { ...selectedFilters };
    delete updated[key];
    onFiltersChange(updated);
  };

  const handleCustomSave = key => {
    const val = customValues[key];
    if (!val?.start_date || !val?.end_date) return;
    handleSelect(key, [
      {
        fullLabel: `${val.start_date}–${val.end_date}`,
        fullValue: val,
        value: val,
        label: `${val.start_date}–${val.end_date}`
      }
    ]);

    setExpandedFilter(null);
  };

  const handleCustomChange = (filterKey, fieldKey, input) => {
    const prev = customValues?.[filterKey]?.[fieldKey] ?? '';

    const isDeletingHyphen = input.length === prev.length - 1 && prev.endsWith('-') && !input.endsWith('-');

    if (isDeletingHyphen) {
      const digits = prev.replace(/\D/g, '').slice(0, -1);
      let formatted = digits;
      if (digits.length > 4) formatted = digits.slice(0, 4) + '-' + digits.slice(4);
      if (digits.length > 6) formatted = formatted.slice(0, 7) + '-' + digits.slice(6);

      setCustomValues(prevState => ({
        ...prevState,
        [filterKey]: {
          ...prevState[filterKey],
          [fieldKey]: formatted
        }
      }));
      return;
    }

    const digits = input.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 3) formatted = digits.slice(0, 4) + '-' + digits.slice(4);
    if (digits.length > 5) formatted = formatted.slice(0, 7) + '-' + digits.slice(6);

    setCustomValues(prevState => ({
      ...prevState,
      [filterKey]: {
        ...prevState[filterKey],
        [fieldKey]: formatted
      }
    }));
  };

  return (
    <View style={styles.container}>
      {filtersConfig.map(filter => {
        const selected = get(selectedFilters, filter.key, []);
        const options = filter.options || [];
        const defaultValue = filter.defaultValue ?? [];

        return (
          <View key={filter.key} style={styles.row}>
            <Text style={styles.label}>{filter.label}</Text>
            {!defaultValue && selected?.length > 0 && (
              <TouchableOpacity onPress={() => handleClear(filter.key)}>
                <Icons.Cross size='S' color='#6b7588' />
              </TouchableOpacity>
            )}

            <View style={styles.selectorContainer}>
              {expandedFilter === filter.key ? (
                <View style={styles.customEditor}>
                  {filter.customEditor?.fields?.map(field => (
                    <View key={field.key} style={styles.fieldRow}>
                      <Text style={styles.fieldLabel}>{field.label}</Text>
                      <PositionModal
                        visible={customOpen}
                        outsideClick={() => setCustomOpen(false)}
                        parentPositions={calendarParentPosition}>
                        <DateRangePicker
                          start_date={customValues[filter.key]?.start_date || null}
                          end_date={customValues[filter.key]?.end_date || null}
                          setSelectionStartDate={date => {
                            handleCustomChange(filter.key, 'start_date', date);
                          }}
                          setSelectionEndDate={date => {
                            handleCustomChange(filter.key, 'end_date', date);
                          }}
                        />
                      </PositionModal>
                      <View style={styles.input}>
                        <TextInputWithAdornment
                          ref={customRef}
                          onChangeText={text => handleCustomChange(filter.key, field.key, text)}
                          onIconClick={() => handleCustomOpen()}
                          value={customValues[filter.key]?.[field.key] || ''}
                        />
                      </View>
                      {/* <TouchableOpacity
                        ref={customRef}
                        style={styles.input}
                        onPress={() => {
                          setCustomOpen(true);
                          handleCustomOpen();
                        }}
                        // onChangeText={text => handleCustomChange(filter.key, field.key, text)}
                      >
                        {customValues[filter.key]?.[field.key] || ''}
                      </TouchableOpacity> */}
                    </View>
                  ))}

                  <View style={styles.customButtons}>
                    <TouchableOpacity onPress={() => setExpandedFilter(null)}>
                      <CancelButton text='Cancel' onPress={() => setExpandedFilter(null)} />
                    </TouchableOpacity>
                    <CancelButton
                      text='Save'
                      disabled={!customValues[filter.key]?.start_date || !customValues[filter.key]?.end_date}
                      onPress={() => handleCustomSave(filter.key)}></CancelButton>
                    {/* <TouchableOpacity
                      onPress={() => handleCustomSave(filter.key)}
                      disabled={!customValues[filter.key]?.start_date || !customValues[filter.key]?.end_date}>
                      <Text
                        style={[
                          styles.saveText,
                          (!customValues[filter.key]?.start_date || !customValues[filter.key]?.end_date) &&
                            styles.saveDisabled
                        ]}>
                        Save
                      </Text> */}
                    {/* </TouchableOpacity> */}
                  </View>
                </View>
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    {filter.type === 'topic' ? (
                      <TopicFilter
                        filter={filter}
                        selected={selected}
                        onChange={val => handleSelect(filter.key, val)}
                      />
                    ) : (
                      <MultiSelect
                        mode={filter.type}
                        filterKey={filter.key}
                        options={options}
                        selected={selected}
                        onSelect={val => handleSelect(filter.key, val)}
                      />
                    )}
                  </View>

                  {filter.customEditor && (
                    <TouchableOpacity onPress={() => setExpandedFilter(filter.key)} style={styles.plusButton}>
                      <Icons.AddIcon size='S' color='#007AFF' />
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default DynamicFilters;

const styles = StyleSheet.create({
  container: {},
  row: {
    flexDirection: 'row',
    alignItems: 'start',
    justifyContent: 'center',
    marginBottom: 2,
    paddingVertical: 5
  },
  label: {
    flex: 1.5,
    fontSize: 14,
    paddingTop: 4,
    color: '#6b7588'
  },
  selectorContainer: {
    flex: 2.5,
    marginRight: 8
  },
  clear: { color: '#666', fontSize: 18, paddingHorizontal: 6 },
  plusButton: {
    marginLeft: 8,
    paddingHorizontal: 4
  },
  customEditor: {
    flex: 'grow',
    borderRadius: 8
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8
  },
  fieldLabel: {
    flex: 1,
    maxWidth: 90,
    fontSize: 13,
    color: '#6b7588'
  },
  input: {
    flex: 2.5,
    fontSize: 14
  },
  customButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 6,
    marginTop: 6
  },
  cancelText: {
    color: '#2563eb',
    fontWeight: '500',
    marginRight: 16
  },
  saveText: {
    color: '#2f78d0',
    fontWeight: '600',
    paddingHorizontal: 8,
    paddingVertical: 4
  },
  saveDisabled: {
    color: '#a1a1aa'
  }
});
